import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  calculateReadingPercent,
  createDefaultRootPrefs,
  getGamePrefs,
  mergeRootPrefs,
  sectionIdForChapter,
  switchActiveVariant,
  toggleChecklistItem,
  toggleExpansionId,
  toggleModuleId,
  toggleSectionRead,
  type GamePrefs,
  type RootGamePrefs,
} from '@shared/state/gamePrefs';
import {
  getAlwaysOnIds,
  getSelectableContentForGame,
  isOptionSelected,
  withDefaultSelection,
  type SelectableContentOption,
} from '@shared/data/selectableContent';
import {
  loadPwaState,
  savePwaState,
  checklistStateFromPrefs,
  type ChecklistState,
} from '../adapters/persistence';

type GamePrefsContextValue = {
  ready: boolean;
  root: RootGamePrefs;
  getPrefs: (gameId: string) => GamePrefs;
  getOptions: (gameId: string) => SelectableContentOption[];
  isSelected: (gameId: string, option: SelectableContentOption) => boolean;
  setExpansionEnabled: (
    gameId: string,
    expansionId: string,
    enabled: boolean,
    storeAsModule?: boolean
  ) => void;
  setActiveVariant: (gameId: string, variantId: string | null) => void;
  toggleRead: (gameId: string, sectionId: string) => void;
  setLastSection: (gameId: string, sectionId: string) => void;
  setChecklistChecked: (
    gameId: string,
    itemId: string,
    checked: boolean
  ) => void;
  patchPrefs: (gameId: string, gamePatch: Partial<GamePrefs>) => void;
  checklistMap: (gameId: string) => ChecklistState;
  readingPercent: (gameId: string, totalSections: number) => number;
  resetGame: (gameId: string) => void;
};

const GamePrefsContext = createContext<GamePrefsContextValue | null>(null);

function gamePrefsEqual(a: GamePrefs, b: GamePrefs): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function GamePrefsProvider({ children }: { children: ReactNode }) {
  const [root, setRoot] = useState<RootGamePrefs>(createDefaultRootPrefs);
  const [ready, setReady] = useState(false);
  const rootRef = useRef(root);
  rootRef.current = root;

  useEffect(() => {
    let cancelled = false;
    void loadPwaState().then((loaded) => {
      if (cancelled) return;
      setRoot(loaded);
      setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const commit = useCallback((next: RootGamePrefs) => {
    setRoot(next);
    rootRef.current = next;
    void savePwaState(next);
  }, []);

  const getPrefs = useCallback((gameId: string) => {
    return withDefaultSelection(gameId, getGamePrefs(rootRef.current, gameId));
  }, []);

  const getOptions = useCallback(
    (gameId: string) => getSelectableContentForGame(gameId),
    []
  );

  const isSelected = useCallback(
    (gameId: string, option: SelectableContentOption) =>
      isOptionSelected(option, getPrefs(gameId)),
    [getPrefs]
  );

  const setExpansionEnabled = useCallback(
    (
      gameId: string,
      expansionId: string,
      enabled: boolean,
      storeAsModule?: boolean
    ) => {
      const current = getPrefs(gameId);
      const alwaysOn = getAlwaysOnIds(gameId);
      const nextPrefs = storeAsModule
        ? toggleModuleId(current, expansionId, enabled, {
            alwaysOnIds: alwaysOn,
          })
        : toggleExpansionId(current, expansionId, enabled);
      if (gamePrefsEqual(current, nextPrefs)) return;
      commit(
        mergeRootPrefs(rootRef.current, {
          gameId,
          gamePatch: nextPrefs,
          lastGameId: gameId,
        })
      );
    },
    [commit, getPrefs]
  );

  const setActiveVariant = useCallback(
    (gameId: string, variantId: string | null) => {
      const current = getPrefs(gameId);
      const nextPrefs = switchActiveVariant(current, variantId);
      if (gamePrefsEqual(current, nextPrefs)) return;
      commit(
        mergeRootPrefs(rootRef.current, {
          gameId,
          gamePatch: nextPrefs,
          lastGameId: gameId,
        })
      );
    },
    [commit, getPrefs]
  );

  const toggleRead = useCallback(
    (gameId: string, sectionId: string) => {
      const current = getPrefs(gameId);
      const nextPrefs = toggleSectionRead(current, sectionId);
      commit(
        mergeRootPrefs(rootRef.current, {
          gameId,
          gamePatch: nextPrefs,
          lastGameId: gameId,
        })
      );
    },
    [commit, getPrefs]
  );

  const setLastSection = useCallback(
    (gameId: string, sectionId: string) => {
      const current = getPrefs(gameId);
      if (
        current.lastSectionId === sectionId &&
        rootRef.current.lastGameId === gameId
      ) {
        return;
      }
      commit(
        mergeRootPrefs(rootRef.current, {
          gameId,
          gamePatch: { lastSectionId: sectionId },
          lastGameId: gameId,
        })
      );
    },
    [commit, getPrefs]
  );

  const setChecklistChecked = useCallback(
    (gameId: string, itemId: string, checked: boolean) => {
      const current = getPrefs(gameId);
      const nextPrefs = toggleChecklistItem(current, itemId, checked);
      if (gamePrefsEqual(current, nextPrefs)) return;
      commit(
        mergeRootPrefs(rootRef.current, {
          gameId,
          gamePatch: nextPrefs,
          lastGameId: gameId,
        })
      );
    },
    [commit, getPrefs]
  );

  const patchPrefs = useCallback(
    (gameId: string, gamePatch: Partial<GamePrefs>) => {
      const current = getPrefs(gameId);
      const merged = mergeRootPrefs(rootRef.current, {
        gameId,
        gamePatch,
        lastGameId: gameId,
      });
      const after = withDefaultSelection(
        gameId,
        getGamePrefs(merged, gameId)
      );
      if (
        gamePrefsEqual(current, after) &&
        rootRef.current.lastGameId === gameId
      ) {
        return;
      }
      commit(merged);
    },
    [commit, getPrefs]
  );

  const checklistMap = useCallback(
    (gameId: string) => checklistStateFromPrefs(getPrefs(gameId)),
    [getPrefs]
  );

  const readingPercent = useCallback(
    (gameId: string, totalSections: number) =>
      calculateReadingPercent(getPrefs(gameId).readSectionIds, totalSections),
    [getPrefs]
  );

  const resetGame = useCallback(
    (gameId: string) => {
      const games = { ...rootRef.current.games };
      delete games[gameId];
      commit({
        ...rootRef.current,
        games,
      });
    },
    [commit]
  );

  const value = useMemo<GamePrefsContextValue>(
    () => ({
      ready,
      root,
      getPrefs,
      getOptions,
      isSelected,
      setExpansionEnabled,
      setActiveVariant,
      toggleRead,
      setLastSection,
      setChecklistChecked,
      patchPrefs,
      checklistMap,
      readingPercent,
      resetGame,
    }),
    [
      ready,
      root,
      getPrefs,
      getOptions,
      isSelected,
      setExpansionEnabled,
      setActiveVariant,
      toggleRead,
      setLastSection,
      setChecklistChecked,
      patchPrefs,
      checklistMap,
      readingPercent,
      resetGame,
    ]
  );

  return (
    <GamePrefsContext.Provider value={value}>
      {children}
    </GamePrefsContext.Provider>
  );
}

export function useGamePrefs(): GamePrefsContextValue {
  const ctx = useContext(GamePrefsContext);
  if (!ctx) {
    throw new Error('useGamePrefs must be used within GamePrefsProvider');
  }
  return ctx;
}

export { sectionIdForChapter };
